import { addCommas } from '@persian-tools/persian-tools';
import type { Coverage, CorporateDoctor, CorporatePlan } from '../types';

/**
 * What the organization pays for this doctor's online visit and what is left for the patient.
 * `useCredit=false` is the invoice switch turned off: the patient keeps the quota and pays in full.
 */
export const getCoverage = (plan: CorporatePlan | null, doctor: Pick<CorporateDoctor, 'priceToman' | 'inNetwork'>, useCredit = true): Coverage => {
  const priceToman = Math.max(0, doctor.priceToman);
  const pays = (status: Coverage['status'], coveredToman = 0): Coverage => ({
    status,
    priceToman,
    coveredToman,
    payableToman: priceToman - coveredToman,
  });

  if (!plan) return pays('none');
  if (plan.kind !== 'budget' && !doctor.inNetwork) return pays('notCovered');
  if (plan.kind === 'quota' && plan.remaining <= 0) return pays('exhausted');
  if (!useCredit) return pays('optedOut');
  if (plan.kind !== 'cap' || priceToman <= plan.capToman) return pays('full', priceToman);
  return pays('partial', plan.capToman);
};

/** The organization pays something for this visit. */
export const isCovered = (coverage: Coverage) => coverage.status === 'full' || coverage.status === 'partial';

/** The doctor is on the list and the credit could apply (the invoice switch has something to switch). */
export const canUseCredit = (plan: CorporatePlan | null, doctor: Pick<CorporateDoctor, 'priceToman' | 'inNetwork'>) =>
  isCovered(getCoverage(plan, doctor, true));

export const formatToman = (toman: number) => addCommas(Math.round(toman));
